import { toast } from "sonner";
import { logger } from "@/shared/lib/logger";
import { posthogCapture } from "@/shared/lib/posthog";
import {
  getFriendlyError,
  getFriendlyErrorMessage,
  getFriendlyErrorTitle,
} from "@/shared/lib/errorMessages";

/**
 * Central error handler.
 *
 * Goals:
 * 1. One call site converts any thrown value into a user-facing toast.
 * 2. Always logs structured context for debugging (never just `e`).
 * 3. Always routes copy through `errorMessages.ts` so phrasing stays consistent.
 * 4. Easy seam to wire Sentry / PostHog later — see TODO below.
 *
 * Usage:
 *   try { ... } catch (e) {
 *     handleError(e, { context: "ChapterUpload.submit", action: "create_chapter" });
 *   }
 *
 * Or to silence the toast (e.g. background poll) and just classify:
 *   handleError(e, { context: "useExpiryAlerts.poll", silent: true });
 */

export type ErrorClassification =
  | "auth"
  | "rate_limit"
  | "server_busy"
  | "timeout"
  | "insufficient_mana"
  | "content_blocked"
  | "validation"
  | "unknown";

export interface HandleErrorOptions {
  /** Where the error happened, e.g. "ChapterUpload.submit". Required-ish. */
  context: string;
  /** Optional logical action label, useful for analytics. */
  action?: string;
  /** Authenticated user id, if known. Helps support reproduce issues. */
  userId?: string;
  /** Override the toast title. Falls back to friendly title. */
  title?: string;
  /** Override the toast description. Falls back to friendly message. */
  description?: string;
  /** If true, no toast is shown (still logs). */
  silent?: boolean;
  /** Treat as validation error (no destructive variant). */
  validation?: boolean;
  /** Extra structured data to log alongside the error. */
  extra?: Record<string, unknown>;
}

export interface HandleErrorResult {
  classification: ErrorClassification;
  title: string;
  description: string;
}

/** The message text of whatever was thrown, for classification and telemetry. */
function rawErrorText(raw: unknown): string {
  if (!raw) return "";
  if (typeof raw === "string") return raw;
  if (raw instanceof Error) return raw.message;
  if (typeof raw === "object") {
    const r = raw as Record<string, unknown>;
    return String(r.message ?? r.error ?? "");
  }
  return String(raw);
}

function classifyForReport(raw: unknown, options: HandleErrorOptions): ErrorClassification {
  if (options.validation) return "validation";
  const text = rawErrorText(raw).toLowerCase();

  if (/insufficient[_\s-]?mana|not enough mana|402/.test(text)) return "insufficient_mana";
  if (/rate[_\s-]?limit|429|too many requests|quota/.test(text)) return "rate_limit";
  if (/timeout|timed out|aborted|abort(?:error)?|network|fetch failed|econnreset/.test(text))
    return "timeout";
  if (/401|403|unauthor|forbidden|jwt|token/.test(text)) return "auth";
  if (/safety|blocked|moderation|policy|prohibited|inappropriate/.test(text))
    return "content_blocked";
  if (/503|server busy|unavailable|overloaded|capacity|empty response|gemini|openai|anthropic/.test(text))
    return "server_busy";
  return "unknown";
}

export function handleError(error: unknown, options: HandleErrorOptions): HandleErrorResult {
  const classification = classifyForReport(error, options);
  const friendly = getFriendlyError(error);
  const title = options.title ?? friendly.title;
  const description = options.description ?? friendly.description;

  // Always log the raw error with structured context. Never let a raw
  // `console.error(e)` leak — it strips the breadcrumbs we need to debug.
  logger.error("[handleError]", {
    context: options.context,
    action: options.action,
    classification,
    userId: options.userId,
    title,
    description,
    raw: error,
    ...(options.extra ?? {}),
  });

  // Consent-gated error telemetry. Before this, a failure the user saw as a
  // toast left no trace we could read — the only prod signal was the handful
  // of admin_alerts the edge functions write. No PII goes out: the call site,
  // the classification and the first 200 chars of the raw message.
  posthogCapture("app_error", {
    context: options.context,
    action: options.action ?? "",
    classification,
    message: rawErrorText(error).slice(0, 200),
  });

  if (!options.silent) {
    if (classification === "validation") {
      toast.warning(title, { description });
    } else {
      toast.error(title, { description });
    }
  }

  return { classification, title, description };
}

/**
 * Convenience: classify + return friendly copy without toasting or logging.
 * Use inside form-field error rendering, or where you control the UI surface.
 */
export function describeError(error: unknown, fallbackDescription?: string) {
  return {
    title: getFriendlyErrorTitle(error),
    description: getFriendlyErrorMessage(error, fallbackDescription),
  };
}
